import contract from './contract.json';
import { validateAppScreens } from './validation';

export type ScreenNode = { type: string; props: Record<string, any> };
export type ScreenData = { root: { props: Record<string, any> }; content: ScreenNode[] };
export type AppScreen = { target: 'custom' | 'home' | 'existing'; slug: string; title: string; menuLabel: string; menuOrder: number; showInMenu: boolean; enabled: boolean; data: ScreenData };
export type AppScreens = { version: 1; screens: AppScreen[] };
export const emptyAppScreens = (): AppScreens => ({ version: 1, screens: [] });
export function readAppScreens(value: unknown): AppScreens {
  return value && !validateAppScreens(value).errors.length ? value as AppScreens : emptyAppScreens();
}
// Existing-screen defaults inherit the release baseline. A non-null runtime
// screen list owns custom-page visibility, so removing a bundled page still works.
export function resolveScreenDefaults(base: AppScreens | null, override: AppScreens | null): AppScreens | null {
  if (!override) return base;
  const slugs = new Set(override.screens.map(screen => screen.slug));
  return { version: 1, screens: [...(base?.screens || []).filter(screen => screen.target !== 'custom' && !slugs.has(screen.slug)), ...override.screens] };
}
export function fieldDefaults(fields: Record<string, { default: unknown }>) {
  return Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.default]));
}
export function createAppScreen(existing: AppScreen[]): AppScreen {
  let index = existing.length + 1;
  while (existing.some(screen => screen.slug === `new-screen-${index}`)) index++;
  return {
    target: 'custom', slug: `new-screen-${index}`, title: 'New screen', menuLabel: 'New screen', menuOrder: existing.length,
    showInMenu: true, enabled: true,
    data: {
      root: { props: fieldDefaults(contract.root) },
      content: [
        { type: 'Heading', props: { id: 'Heading-welcome', text: 'Care, connected.', size: 'title', align: 'left' } },
        { type: 'Text', props: { id: 'Text-intro', text: 'A place for the things that matter to your care team.', maxLines: 0, align: 'left' } },
        { type: 'Card', props: { id: 'Card-next', padding: 0, gap: 0, content: [
          { type: 'NavigationRow', props: { id: 'NavigationRow-plans', label: 'Your care plans', description: 'Pick up where you left off', destination: 'plans' } },
        ] } },
      ],
    },
  };
}
export function createHomeLayout(): AppScreen {
  return {
    target: 'home', slug: 'home', title: 'Home', menuLabel: 'Home', menuOrder: 0, showInMenu: false, enabled: true,
    data: { root: { props: { padding: 16, gap: 16, maxWidth: 720 } }, content: [
      { type: 'HomeWelcome', props: { id: 'HomeWelcome-main' } },
      { type: 'HomeSetup', props: { id: 'HomeSetup-main' } },
      { type: 'HomeNextActions', props: { id: 'HomeNextActions-main' } },
    ] },
  };
}
export function createExistingLayouts(): AppScreen[] {
  return [createHomeLayout(), ...Object.entries(contract.existingScreens).map(([slug, screen]) => ({
    target: 'existing' as const, slug, title: screen.title, menuLabel: screen.title, menuOrder: 0, showInMenu: false, enabled: true,
    data: { root: { props: { padding: 0, gap: 0, maxWidth: 1600 } }, content: screen.sections.map(type => ({
      type, props: { id: `${type}-main`, ...fieldDefaults(contract.components[type as keyof typeof contract.components].fields) },
    })) },
  }))];
}
export const screenMenu = (config: AppScreens) => config.screens.filter(screen => screen.target === 'custom' && screen.enabled && screen.showInMenu)
  .sort((a, b) => a.menuOrder - b.menuOrder || a.menuLabel.localeCompare(b.menuLabel));
